import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUploadStatusApiDocumentsUploadJobIdGet,
  type JobPhase,
  type IngestionJobStatusResponse,
} from "@noa/api-core";
import { UploadQueryKey } from "../../query-keys";

const TERMINAL_PHASES: JobPhase[] = ["completed", "failed"];

export function useUploadJobStatus(jobId: string | null, options?: { includeFiles?: boolean }) {
  const queryClient = useQueryClient();
  const includeFiles = options?.includeFiles ?? false;

  const { data, isPending, isError, error, refetch } = useQuery<IngestionJobStatusResponse>({
    queryKey: [...UploadQueryKey.job(jobId ?? ""), includeFiles],
    queryFn: async () => {
      const { data } = await getUploadStatusApiDocumentsUploadJobIdGet({
        path: { job_id: jobId! },
        query: { include_files: includeFiles },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      // Poll every 2s until terminal phase
      const phase = query.state.data?.phase;
      return phase && TERMINAL_PHASES.includes(phase) ? false : 2000;
    },
  });

  const isTerminal = data?.phase ? TERMINAL_PHASES.includes(data.phase) : false;

  // Gate with a ref so this fires exactly once at the pending→terminal transition,
  // not on every later observer re-evaluation (refocus, remount) while terminal.
  const hasInvalidatedRef = useRef(false);
  useEffect(() => {
    if (!isTerminal) {
      hasInvalidatedRef.current = false;
      return;
    }
    if (hasInvalidatedRef.current) return;
    hasInvalidatedRef.current = true;
    void queryClient.invalidateQueries({ queryKey: UploadQueryKey.list() });
  }, [isTerminal, queryClient]);

  return {
    status: data,
    isLoading: isPending,
    isError,
    error,
    refetch,
    isTerminal,
  };
}
